"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StoryWebPlayer = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/* eslint-disable max-lines */
const web_core_ui_1 = require("@snapchat/web-core-ui");
const web_moderation_1 = require("@snapchat/web-moderation");
const web_sharing_1 = require("@snapchat/web-sharing");
const web_blizzard_reporting_1 = require("@snapchat/web-blizzard-reporting");
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const isEqual_1 = __importDefault(require("lodash/isEqual"));
const react_1 = require("react");
const constants_1 = require("../../constants");
const playerConstants_1 = require("../../playerConstants");
const utils_1 = require("../../utils");
const Cards_1 = require("../Cards");
const SnapNotAvailable_1 = require("../SnapNotAvailable");
const StoryWebPlayer_module_css_1 = __importDefault(require("./StoryWebPlayer.module.css"));
const VideoPlayer_1 = require("./VideoPlayer");
const metrics_1 = require("../../lib/metrics");
const web_graphene_reporting_1 = require("@snapchat/web-graphene-reporting");
const ProgressCard_1 = require("../Cards/ProgressCard");
const web_env_1 = require("@snapchat/web-env");
// TODO (https://jira.sc-corp.net/browse/WEB-999): Change into Functional Component
// eslint-disable-next-line react/prefer-stateless-function
class StoryWebPlayer extends react_1.PureComponent {
    snapViewed;
    snapFullViewed;
    visibilityChange = (0, web_core_ui_1.getBrowserVisibilityProp)() || "visibilitychange";
    // For images, this is the delay before switching to the next snap
    nextSnapTimer = undefined;
    constructor(props) {
        super(props);
        // Used to track unique snap views
        this.snapViewed = {};
        // Used to track unique snap full views
        this.snapFullViewed = {};
        this.state = {
            analyticsSnapViewsByIndex: {},
            isOnPlay: props.autoPlay || false,
            isImage: false,
            isMuted: props.isMuted || false,
            showShareSheet: web_sharing_1.ShareSheetPopupState.INVISIBLE,
            shareSheetOpenTime: 0,
            isHls: false,
            seekTimeMs: undefined,
            showMenuItems: false,
        };
        this.incrementGrafanaMetricsCounter = this.incrementGrafanaMetricsCounter.bind(this);
    }
    onNewSnap = () => {
        this.stopImageNextTimeout();
        this.reportSnapViewMetricsForGrafana(this.props.story.type);
        const { story } = this.props;
        const currentIndex = this.props.currentOnPlaySnapIndex;
        const currentSnap = story.snaps?.[currentIndex];
        const isImage = currentSnap?.snapMediaType === constants_1.SnapMediaType.Image;
        // the last snap in Show stories are "subscribe snaps" which are independent of the video track URL
        const isSubscribeSnap = currentIndex + 1 === story.snaps?.length && Boolean(currentSnap?.snapUrls?.mediaUrl);
        // favour HLS playback if we have a video track URL
        const isHls = !isImage &&
            Boolean(story.videoTrackUrl) &&
            // the last snap in HLS stories are independent of the video track URL
            // favour the snap's media URL in this case
            !isSubscribeSnap;
        const seekTimeMs = currentSnap?.intervalStartTimeMs;
        this.setState({ isImage, isHls, seekTimeMs }, () => {
            this.stopImageNextTimeout();
            this.playSnap();
        });
    };
    onVisibilityChange = () => {
        // ignore logic if the story has ended (and replay button is visible).
        if (this.props.shouldShowReplayStory) {
            return;
        }
        if (!(0, web_core_ui_1.getWindowVisibility)()) {
            this.pauseSnap();
        }
        else {
            this.playSnap();
        }
    };
    componentDidMount() {
        if (!this.props.autoPlay) {
            return;
        }
        this.setupTogglePlayWithFocus();
    }
    setupTogglePlayWithFocus() {
        this.onNewSnap();
        if (this.props.shouldTogglePlayWithFocus) {
            (0, web_env_1.getDocument)()?.addEventListener(this.visibilityChange, this.onVisibilityChange, false);
        }
    }
    componentWillUnmount() {
        this.stopImageNextTimeout();
        if (this.props.shouldTogglePlayWithFocus) {
            (0, web_env_1.getDocument)()?.removeEventListener(this.visibilityChange, this.onVisibilityChange);
        }
    }
    componentDidUpdate(prevProps) {
        if (prevProps.autoPlay !== this.props.autoPlay) {
            if (this.props.autoPlay) {
                this.setupTogglePlayWithFocus();
            }
            else {
                this.pauseSnap();
                if (this.props.shouldTogglePlayWithFocus) {
                    (0, web_env_1.getDocument)()?.removeEventListener(this.visibilityChange, this.onVisibilityChange);
                }
            }
        }
        // Replay story visibility changed
        if (prevProps.shouldShowReplayStory !== this.props.shouldShowReplayStory) {
            if (this.props.shouldShowReplayStory) {
                this.pauseSnap();
            }
            else {
                this.onNewSnap();
            }
        }
        // If the story has changed
        if (!(0, isEqual_1.default)(prevProps.story, this.props.story)) {
            // TODO: This should be one of "TAP", "SWIPE_UP" or "SWIPE_DOWN"
            this.stopImageNextTimeout();
            this.setSnapViewEndTime({
                exitEvent: constants_1.ExitEvents.TAP,
                isFullView: this.isFullView(constants_1.ExitEvents.TAP),
                resetSnapViews: false,
            });
            if (this.props.autoPlay) {
                this.onNewSnap();
            }
        }
        else if (prevProps.currentOnPlaySnapIndex !== this.props.currentOnPlaySnapIndex) {
            this.onNewSnap();
        }
    }
    /**
     * Video Player Controls
     */
    toggleMute = () => {
        if (this.props.overrideMute) {
            this.props.overrideMute.toggleMute();
        }
        else {
            this.setState((state) => ({
                isMuted: !state.isMuted,
            }));
        }
    };
    playVideo = () => {
        this.setState({ isOnPlay: true });
    };
    pauseVideo = () => {
        this.setState({ isOnPlay: false });
    };
    // eslint-disable-next-line class-methods-use-this
    isFullView = (exitEvent) => exitEvent === constants_1.ExitEvents.AUTO_ADVANCE;
    // We consider every snap play as a snap view
    reportSnapViewMetricsForGrafana = (storyType) => this.logSnapViewMetrics(this.snapViewed, `SnapViews.${storyType}`);
    // If a snap has been played completely, we count this towards a full snap view
    reportFullSnapViewMetricsForGrafana = (analyticsSnapView, storyType) => {
        if (!analyticsSnapView.fullView) {
            return;
        }
        this.logSnapViewMetrics(this.snapFullViewed, `FullSnapViews.${storyType}`);
    };
    logSnapViewMetrics = (snapViewed, metricName) => {
        const device = this.props.isMobile ? "Mobile" : "Desktop";
        void this.incrementGrafanaMetricsCounter(`${this.props.grafanaMetrics.pageName}.${device}.Total${metricName}`);
        const snapId = this.props.story.snaps?.[this.props.currentOnPlaySnapIndex]?.id || "";
        // Check if this is the first time it is being viewed
        if (snapViewed[snapId]) {
            // We have already logged a unique snap view.
            return;
        }
        snapViewed[snapId] = true;
        void this.incrementGrafanaMetricsCounter(`${this.props.grafanaMetrics.pageName}.${device}.Unique${metricName}`);
    };
    logStoryEnd = (analyticsSnapViewsByIndex, exitEvent, resetSnapViews) => {
        if (this.props.currentOnPlaySnapIndex < 0) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const currentSnapViews = analyticsSnapViewsByIndex[this.props.currentOnPlaySnapIndex] || [];
        if (currentSnapViews.length === 0) {
            // Nothing to report
            return;
        }
        this.reportFullSnapViewMetricsForGrafana(currentSnapViews[currentSnapViews.length - 1], this.props.story.type);
        // Always log the latest snap view
        (0, metrics_1.reportStorySnapViewWeb)(currentSnapViews[currentSnapViews.length - 1], this.props.story, this.props.currentOnPlaySnapIndex, exitEvent);
        // If this is not the last snap of the story, don't log the story view.
        if (this.props.currentOnPlaySnapIndex !== (this.props.story.snaps?.length ?? 0) - 1) {
            return;
        }
        (0, metrics_1.reportStoryStoryViewWeb)(analyticsSnapViewsByIndex, this.props.story, exitEvent);
        // Reset the snap views.
        if (resetSnapViews) {
            this.setState({ analyticsSnapViewsByIndex: {} });
        }
    };
    // eslint-disable-next-line class-methods-use-this
    getStateWithSnapViewStartTime = (prevState, props) => {
        const currentSnapViews = [
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            ...(prevState.analyticsSnapViewsByIndex[props.currentOnPlaySnapIndex] || []),
            { startTime: Date.now() },
        ];
        return {
            ...prevState.analyticsSnapViewsByIndex,
            [props.currentOnPlaySnapIndex]: currentSnapViews,
        };
    };
    onPlay = () => {
        this.setState((prevState, props) => ({
            analyticsSnapViewsByIndex: this.getStateWithSnapViewStartTime(prevState, props),
        }));
        this.props.onPlaySuccessful?.();
    };
    getStateWithSnapViewEndTime = (prevState, props, isFullView) => {
        const currentSnapViews = 
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        this.state.analyticsSnapViewsByIndex[props.currentOnPlaySnapIndex] ?? [];
        if (currentSnapViews.length === 0 || currentSnapViews[currentSnapViews.length - 1].endTime) {
            // Nothing to update
            return prevState.analyticsSnapViewsByIndex;
        }
        currentSnapViews[currentSnapViews.length - 1].endTime = Date.now();
        currentSnapViews[currentSnapViews.length - 1].fullView = isFullView;
        return {
            ...prevState.analyticsSnapViewsByIndex,
            [props.currentOnPlaySnapIndex]: currentSnapViews,
        };
    };
    setSnapViewEndTime = ({ exitEvent, isFullView, resetSnapViews, }) => {
        this.setState((prevState, props) => ({
            analyticsSnapViewsByIndex: this.getStateWithSnapViewEndTime(prevState, props, isFullView),
        }), () => {
            if (exitEvent) {
                this.logStoryEnd((0, cloneDeep_1.default)(this.state.analyticsSnapViewsByIndex), exitEvent, resetSnapViews);
            }
        });
    };
    onEnded = () => {
        this.setSnapViewEndTime({
            exitEvent: constants_1.ExitEvents.AUTO_ADVANCE,
            isFullView: this.isFullView(constants_1.ExitEvents.AUTO_ADVANCE),
            resetSnapViews: false,
        });
        if (this.props.loopSnap) {
            this.reportSnapViewMetricsForGrafana(this.props.story.type);
        }
        else {
            this.props.onPlayEnded?.();
        }
    };
    startImageNextTimeout = () => {
        this.nextSnapTimer = setTimeout(() => {
            this.onEnded();
        }, playerConstants_1.IMAGE_TIMEOUT_DURATION_MS);
    };
    // clears the timeout meant for images
    stopImageNextTimeout = () => {
        this.nextSnapTimer && clearTimeout(this.nextSnapTimer);
    };
    pauseSnap = () => {
        if (this.state.isImage) {
            this.stopImageNextTimeout();
            this.setState({ isOnPlay: false });
        }
        else {
            this.pauseVideo();
        }
    };
    playSnap = () => {
        this.stopImageNextTimeout();
        if (this.state.isImage) {
            this.startImageNextTimeout();
            this.setState({ isOnPlay: true });
        }
        else {
            this.playVideo();
        }
    };
    togglePlay = () => {
        if (this.state.isOnPlay) {
            this.pauseSnap();
        }
        else {
            this.playSnap();
        }
    };
    playNextSnapWithMetric = () => {
        if (this.props.playNextSnap) {
            this.props.playNextSnap();
            this.setSnapViewEndTime({
                exitEvent: constants_1.ExitEvents.TAP,
                isFullView: this.isFullView(constants_1.ExitEvents.TAP),
                resetSnapViews: false,
            });
        }
    };
    playPrevSnapWithMetric = () => {
        if (this.props.playPrevSnap) {
            this.props.playPrevSnap();
            this.setSnapViewEndTime({
                exitEvent: constants_1.ExitEvents.TAP,
                isFullView: this.isFullView(constants_1.ExitEvents.TAP),
                resetSnapViews: false,
            });
        }
    };
    onPause = () => {
        this.props.onPause?.();
        this.setSnapViewEndTime({
            isFullView: false,
            resetSnapViews: false,
        });
    };
    onError = (error) => {
        this.props.onPlayFailed?.(error);
        console.error(`Playing error: `, error, this.state.isOnPlay);
    };
    renderMedia = (mediaSrc, poster) => {
        if (this.state.isImage) {
            return ((0, jsx_runtime_1.jsx)("img", { src: mediaSrc, className: StoryWebPlayer_module_css_1.default.media, "data-testid": "playerContentImg", 
                // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                alt: "Snap image" }));
        }
        return ((0, jsx_runtime_1.jsx)(VideoPlayer_1.VideoPlayer, { className: StoryWebPlayer_module_css_1.default.videoPlayer, isMuted: this.props.overrideMute ? this.props.overrideMute.isMuted : this.state.isMuted, src: mediaSrc, isPlaying: this.state.isOnPlay, autoplay: this.props.autoPlay ?? false, poster: poster, isHls: this.state.isHls, onPlay: this.onPlay, onEnded: this.onEnded, seekTimeMs: this.state.seekTimeMs, onError: this.onError, loopSnap: this.props.loopSnap, onPause: this.onPause, emitPlaybackMetric: this.emitPlaybackMetric }));
    };
    handleShareSheetOpen = () => {
        void this.incrementGrafanaMetricsCounter(`${this.props.grafanaMetrics.pageName}.Share.ShareSheet.Opened`);
        this.pauseSnap();
        this.setState((state) => ({
            ...state,
            showShareSheet: web_sharing_1.ShareSheetPopupState.VISIBLE,
            shareSheetOpenTime: Date.now(),
        }));
    };
    handleShareSheetClose = () => {
        this.setState((state) => ({
            ...state,
            showShareSheet: web_sharing_1.ShareSheetPopupState.INVISIBLE,
            shareSheetOpenTime: 0,
        }));
    };
    async incrementGrafanaMetricsCounter(counterName) {
        const { metricsPrefix } = this.props.grafanaMetrics;
        return (0, web_graphene_reporting_1.incrementCounter)(`${metricsPrefix}.${counterName}`);
    }
    renderShareSheet = () => ((0, jsx_runtime_1.jsx)(web_sharing_1.ShareSheetPopup, { incrementCounter: this.incrementGrafanaMetricsCounter, pageName: this.props.grafanaMetrics.pageName, pageType: this.props.pageType || web_blizzard_reporting_1.PageType.UNSET, url: this.props.contentMetadata?.shareUrl || "https://www.snapchat.com/discover", title: this.props.shareTitle, onClose: this.handleShareSheetClose, isVisible: this.state.showShareSheet, sessionOpenTime: this.state.shareSheetOpenTime }));
    emitPlaybackMetric = (metricSuffix) => {
        void this.incrementGrafanaMetricsCounter(`${this.props.grafanaMetrics.pageName}.StoryWebPlayer.VideoPlayer.${metricSuffix}`);
    };
    toggleShowMenuItems = (event) => {
        event.stopPropagation();
        this.setState((state) => ({
            ...state,
            showMenuItems: !state.showMenuItems,
        }));
    };
    closeMenu = () => {
        this.setState((state) => ({
            ...state,
            showMenuItems: false,
        }));
    };
    render() {
        if (!(0, utils_1.hasSnaps)(this.props.story) && !this.props.story.videoTrackUrl) {
            return ((0, jsx_runtime_1.jsx)("div", { className: StoryWebPlayer_module_css_1.default.contentWrapper, children: (0, jsx_runtime_1.jsx)(SnapNotAvailable_1.SnapNotAvailable, {}) }));
        }
        const currentSnap = this.props.story.snaps?.[this.props.currentOnPlaySnapIndex];
        // HLS video URLs (i.e. videoTrackURL) are not prefetched
        const prefetchedSnap = currentSnap?.id && !this.props.story.videoTrackUrl
            ? this.props.feedRegistryRef?.current[currentSnap.id]
            : undefined;
        this.props.prefetchingMetrics?.emitPrefetchingMetrics(this.props.isMobile, prefetchedSnap);
        const imgSrc = prefetchedSnap?.previewBlob?.blobUrl ?? currentSnap?.snapUrls?.mediaPreviewUrl;
        const poster = this.props.shouldShowPosterImage ? imgSrc : undefined;
        const mediaSrc = this.props.story.videoTrackUrl ||
            prefetchedSnap?.mediaBlob?.blobUrl ||
            currentSnap?.snapUrls?.mediaUrl ||
            "";
        const allowReporting = (this.props.allowReporting && Boolean(currentSnap?.id)) || false;
        const hasVideos = this.props.story.snaps?.find((snap) => snap?.snapMediaType === constants_1.SnapMediaType.Video) !==
            undefined;
        const hasMultipleSnaps = (this.props.story.snaps?.length || 0) > 1;
        return ((0, jsx_runtime_1.jsxs)("div", { className: StoryWebPlayer_module_css_1.default.contentWrapper, "data-testid": "storyWebPlayer", children: [this.renderMedia(mediaSrc, poster), !this.props.hideControls && ((0, jsx_runtime_1.jsx)(Cards_1.ControlsCard, { togglePlay: this.togglePlay, isOnPlay: this.state.isOnPlay, pageName: this.props.grafanaMetrics.pageName, playNextSnap: this.playNextSnapWithMetric, playPrevSnap: this.playPrevSnapWithMetric, canPlayNextSnap: this.props.canPlayNextSnap, canPlayPrevSnap: this.props.canPlayPrevSnap, incrementCounter: this.incrementGrafanaMetricsCounter, replayStory: this.props.replayStory, shouldShowReplayStory: this.props.shouldShowReplayStory, isMuted: this.props.overrideMute ? this.props.overrideMute.isMuted : this.state.isMuted, showMuteButton: hasVideos, toggleMute: this.toggleMute, isMobile: this.props.isMobile, shareUrl: this.props.contentMetadata?.shareUrl || "", shareTitle: this.props.contentMetadata?.creatorUsername || "", shareSheetHandler: this.handleShareSheetOpen, shouldRenderShareSheet: this.props.shouldRenderShareSheet, verticalOffset: this.props.verticalOffset, allowReporting: allowReporting, toggleShowMenuItems: this.toggleShowMenuItems, embedShareProps: this.props.embedShareProps })), this.props.showProgress && hasMultipleSnaps && ((0, jsx_runtime_1.jsx)(ProgressCard_1.ProgressCard, { currentIndex: this.props.currentOnPlaySnapIndex, totalCount: this.props.story.snaps?.length || 0 })), this.props.children, this.state.showShareSheet !== web_sharing_1.ShareSheetPopupState.INVISIBLE && this.renderShareSheet(), this.props.allowReporting && currentSnap?.id && ((0, jsx_runtime_1.jsx)(web_moderation_1.SnapReportingCard, { snapId: currentSnap.id, storyType: this.props.story.type, playNext: this.props.playNextSnap, isMobile: this.props.isMobile, pauseSnap: this.pauseSnap, playSnap: this.playSnap, playlistEnded: this.props.shouldShowReplayStory || false, locale: this.props.locale, onReportSubmit: this.props.onReportSubmit, onShepherdReportSubmit: this.props.onShepherdReportSubmit, showMenuItems: this.state.showMenuItems, closeMenu: this.closeMenu, shepherd: this.props.onShepherdReportSubmit !== undefined, verticalOffset: this.props.verticalOffset }))] }));
    }
}
exports.StoryWebPlayer = StoryWebPlayer;
//# sourceMappingURL=StoryWebPlayer.js.map